import classNames from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { Status } from '../../../../shared/builds/types';
import { FREE_PLAN_NAME } from '../../../../shared/subscriptions/consts';
import { usePlanSelectorContext } from '../../../contexts/PlanSelectorContext';
import { useRouter } from '../../../contexts/RouterContext';
import { isAuthed, isProjectInCurrentOrg } from '../../../utils';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import type { Project } from '../../../types';
import { plural } from '../../../../shared/helpers/plural';

type Props = {
  count: number;
  baseUrl: string;
  projectOrgId?: Project['organizationId'];
};

export const UnknownDependenciesListItem: VoidFunctionComponent<Props> = ({
  count,
  baseUrl,
  projectOrgId,
}) => {
  const router = useRouter();
  const { showPlanSelectorModal } = usePlanSelectorContext();
  const isInCurrentOrg = isProjectInCurrentOrg(projectOrgId ?? 0);

  const onItemClick = () => {
    if (!isAuthed()) {
      return showPlanSelectorModal({ tier: FREE_PLAN_NAME });
    }
    if (!isInCurrentOrg) {
      return null;
    }
    return router.push(`${baseUrl}/browse/dependencies?status=${Status.Unknown}`);
  };

  if (!count) {
    return null;
  }

  return (
    <li className="issue-list-item upshadow">
      <button
        type="button"
        className="unstyled block width-full text-align-left"
        aria-disabled={isAuthed() && !isInCurrentOrg}
        onClick={onItemClick}
      >
        <span className="icon-circle" />
        <Flex align="center" justify="space-between">
          <h4>
            {count} Unknown {plural(count, 'Dependency', 'Dependencies')}
          </h4>
          <div className="text-right">
            {/* Visual-only: the whole item is the button, and buttons can't be nested. */}
            <span className={classNames('btn btn-default', { disabled: !isInCurrentOrg })}>
              {isInCurrentOrg ? (
                <>
                  <Icon icon={icons.reviewFix} /><span className="margin-left-xs full">Review & Fix</span>
                </>
              ) : (
                <>
                  <Icon icon={icons.lock} /> Maintainer Access Required
                </>
              )}
            </span>
            <span className="margin-left-xs">
              <Icon icon={icons.chevronDown} />
            </span>
          </div>
        </Flex>
        <p>
          FOSSA was unable to analyze these dependencies. They may be behind a private registry or use an unsupported package manager.
        </p>
      </button>
    </li>
  );
};
